/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Compact Console Layout for Admin Backoffice (Task 6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  ExternalLink,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { AdminHeader } from '../../components/layout/AdminHeader';
import {
  ADMIN_NAV_ITEMS,
  type AdminViewId,
} from '../../components/layout/AdminSidebar';
import { useI18n } from '../../i18n';
import { useTheme } from '../../context/ThemeProvider';
import { getAdminDomains } from '../../lib/urls';
import { resolveViewToPath } from '../../routes/admin-routes';

export interface CompactLayoutProps {
  activeView: AdminViewId;
  onViewChange: (view: AdminViewId) => void;
  children: React.ReactNode;
}

export const CompactLayout: React.FC<CompactLayoutProps> = ({
  activeView,
  onViewChange,
  children,
}) => {
  const { t } = useI18n();
  const { branding, isDark } = useTheme();
  const domains = getAdminDomains();

  const logoUrl = isDark
    ? (branding?.logoDarkUrl || branding?.logoLightUrl)
    : (branding?.logoLightUrl || branding?.logoDarkUrl);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background-base text-foreground font-sans">
      {/* 1. Ultra-Compact 56px Sidebar */}
      <aside className="w-14 min-w-[56px] max-w-[56px] flex flex-col items-center border-r border-surface-border bg-surface select-none z-20">
        {/* Brand Logo Icon */}
        <div className="h-16 w-full flex items-center justify-center border-b border-surface-border">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={branding?.siteName || 'Sophia Admin'}
              className="w-8 h-8 object-contain rounded-lg"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-8 h-8 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white font-bold text-sm shadow-glow-primary"
            style={logoUrl ? { display: 'none' } : undefined}
            title={branding?.siteName || 'Sophia Admin'}
          >
            <SophiaIcon name="structure-server" size={18} className="text-white" />
          </div>
        </div>

        {/* Compact Navigation Icon List */}
        <div className="flex-1 w-full py-3 px-1.5 space-y-1 overflow-y-auto no-scrollbar">
          {ADMIN_NAV_ITEMS.map((item) => {
            const isActive = activeView === item.id;
            const label = t(item.labelKey);
            const itemPath = resolveViewToPath(item.id);

            return (
              <div key={item.id} className="relative group flex justify-center">
                <a
                  href={itemPath}
                  onClick={(e) => {
                    if (!e.metaKey && !e.ctrlKey && e.button === 0) {
                      e.preventDefault();
                      onViewChange(item.id);
                    }
                  }}
                  aria-label={label}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-primary/10 text-primary font-semibold shadow-sm border border-primary/25'
                      : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
                  }`}
                >
                  <SophiaIcon
                    name={item.sophiaIcon}
                    size={16}
                    className={isActive ? 'text-primary' : 'text-muted-fg group-hover:text-foreground'}
                  />
                </a>

                {/* Floating Tooltip */}
                <div className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1.5 rounded-lg bg-surface border border-surface-border shadow-xl text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 flex items-center gap-1.5">
                  <span>{label}</span>
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Compact Bottom Utility / External Links */}
        <div className="py-3 px-1.5 border-t border-surface-border w-full flex flex-col items-center space-y-1 bg-surface-elevated/40">
          <div className="relative group flex justify-center w-full">
            <a
              href={domains.appOrigin}
              target="_blank"
              rel="noreferrer"
              aria-label="User Workspace"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
            >
              <SophiaIcon name="brainstorm" size={16} />
            </a>
            <div className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1.5 rounded-lg bg-surface border border-surface-border shadow-xl text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 flex items-center gap-1">
              <span>User Workspace</span>
              <ExternalLink className="w-3 h-3 text-muted-fg" />
            </div>
          </div>

          <div className="relative group flex justify-center w-full">
            <a
              href={domains.siteOrigin}
              target="_blank"
              rel="noreferrer"
              aria-label="Landing Page"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
            >
              <SophiaIcon name="hologram" size={16} />
            </a>
            <div className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1.5 rounded-lg bg-surface border border-surface-border shadow-xl text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 flex items-center gap-1">
              <span>Landing Page</span>
              <ExternalLink className="w-3 h-3 text-muted-fg" />
            </div>
          </div>
        </div>
      </aside>

      {/* 2. Main High-Density Workspace (Full-Width Tables Maximized) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminHeader activeView={activeView} />

        {/* Full-width container to maximize table viewport for massive record monitoring */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="w-full space-y-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

